import gsap from 'gsap'
import {Group, Vector3, Vector4} from 'three'

import type {BrandedDividerParams} from '../../components/WebGLContext/WebGLContext.types'
import {getRenderer} from '../_commonObjects'
import {canvasSize, windowSize} from '../_config'
import {prefersReducedMotion} from '../_motionUtils'
import {BREAKPOINT, PADDING} from '../_rawConfig'
import InvertocatCube from '../modules/cube/InvertocatCube'
import RoundedCubeGeometry from '../modules/cube/RoundedCubeGeometry'
import SecurityCube from '../modules/cube/SecurityCube'
import groupSizes from './_groupSizes'
import Copilot0 from './characters/copilot/Copilot0'
import Copilot1 from './characters/copilot/Copilot1'
import Copilot2 from './characters/copilot/Copilot2'
import invertocat from './characters/invertocat'
import Mona0 from './characters/mona/Mona0'
import Mona1 from './characters/mona/Mona1'
import Mona2 from './characters/mona/Mona2'
import Cubes from './Cubes'
import RainbowMozaicCube from './RainbowMozaicCube'

export default class Layout {
  params: BrandedDividerParams | null = null
  group: Group = new Group()
  _cubeGeometry: RoundedCubeGeometry
  tl: gsap.core.Timeline = gsap.timeline({paused: true})
  rainbowMozaicCube: RainbowMozaicCube | null = null
  securityCube: SecurityCube | null = null
  copilot: Copilot0 | Copilot1 | Copilot2 | null = null
  mona: Mona0 | Mona1 | Mona2 | null = null
  invertocatCube: InvertocatCube | null = null
  groupScale: number = 25
  isAssetLoaded: boolean = false
  isAnimationStarted: boolean = false
  cubes: Cubes | null = null
  scrollProgress: number = 0
  constructor(_cubeGeometry: RoundedCubeGeometry) {
    this._cubeGeometry = _cubeGeometry
  }
  async createScene(element: HTMLElement, id: string, params: BrandedDividerParams) {
    this.group.userData.ditherId = id
    this.group.userData.domElement = element
    this.params = params

    this.group.userData.rectData = new Vector4(1, 1, 1, 1) // width, height, left, top
    this.group.userData._rectData = new Vector4(1, 1, 1, 1) // width, height, left, top

    this.cubes = new Cubes(this._cubeGeometry, this.group, this.tl)
    this.cubes.createCubes(params)
    this.group.add(this.cubes.group)

    if (params.character === 'mona') {
      switch (params.layout) {
        case 0:
          this.mona = new Mona0(params, this.group.userData.rectData)
          await this.mona.create(this.group, this.tl)
          break
        case 1:
          this.mona = new Mona1(params, this.group.userData.rectData)
          await this.mona.create(this.group, this.tl)
          break
        case 2:
          this.mona = new Mona2(params, this.group.userData.rectData)
          await this.mona.create(this.group, this.tl)
          break
      }
    }

    if (params.character === 'copilot') {
      switch (params.layout) {
        case 0:
          this.copilot = new Copilot0(params, this.group.userData.rectData)
          await this.copilot.create(this.group, this.tl)
          break
        case 1:
          this.copilot = new Copilot1(params, this.group.userData.rectData)
          await this.copilot.create(this.group, this.tl)
          break
        case 2:
          this.copilot = new Copilot2(params, this.group.userData.rectData)
          await this.copilot.create(this.group, this.tl)
          break
      }
    }

    if (params.character === 'security') {
      this.securityCube = new SecurityCube(params, this._cubeGeometry, this.group.userData.rectData)
      await this.securityCube.create(this.cubes.group2, this.tl)
    }

    if (params.character === 'invertocat') {
      const invertocatParams = {
        rotation: new Vector3(),
        position: new Vector3(),
      }

      switch (params.layout) {
        case 0:
          invertocatParams.rotation.set(0, Math.PI * 0.5, -Math.PI * 0.5)
          invertocatParams.position.set(0.67, 0, 0)
          break
        case 1:
          invertocatParams.rotation.set(0, 0, Math.PI)
          invertocatParams.position.set(0, 0, 0.67)
          break
        case 2:
          invertocatParams.rotation.set(0, Math.PI * 0.5, Math.PI)
          invertocatParams.position.set(0.67, 0, 0)
          break
      }

      this.invertocatCube = new InvertocatCube(
        {
          cubeConfig: invertocat[params.layout]!,
          cubeGeometry: this._cubeGeometry,
          rectData: this.group.userData.rectData,
          _rectData: this.group.userData._rectData,
        },
        invertocatParams,
      )
      this.invertocatCube.create(this.cubes.group2)
    }

    if (params.character === 'mona' && params.layout === 0) {
      this.rainbowMozaicCube = new RainbowMozaicCube(this._cubeGeometry)
      this.rainbowMozaicCube.create(this.group, this.tl)
    }

    this.isAssetLoaded = true
  }

  resize() {
    this.updateRectData()
    if (this.cubes) {
      this.cubes.resize()
    }
  }

  updateRectData() {
    const ele = this.group.userData.domElement as HTMLElement
    const rect = ele.getBoundingClientRect()

    this.group.userData._rectData.set(
      rect.width,
      rect.height,
      rect.left,
      canvasSize.y - rect.top - windowSize.y * PADDING,
    )

    this.group.userData.rectData.copy(this.group.userData._rectData)
    const renderer = getRenderer()
    if (renderer) {
      this.group.userData.rectData.multiplyScalar(renderer.getPixelRatio())
    }

    return rect
  }

  update(_delta: number) {
    if (!this.params) return
    const reducedMotion = prefersReducedMotion()
    const _groupSize = groupSizes[this.params.character][this.params.layout]!
    let groupScale = _groupSize.desktop.scale
    let groupPosition = _groupSize.desktop.position

    if (window.innerWidth <= BREAKPOINT.MOBILE) {
      groupScale = _groupSize.mobile.scale
      groupPosition = _groupSize.mobile.position
    } else if (window.innerWidth <= BREAKPOINT.TABLET) {
      groupScale = _groupSize.table.scale
      groupPosition = _groupSize.table.position
    }

    const rect = this.updateRectData()
    // y -= rect.height / 4

    const x = rect.left + rect.width / 2 - windowSize.x / 2
    const y = -(rect.top + rect.height / 2 - windowSize.y / 2)

    const scale = rect.width / groupScale
    this.group.scale.set(scale, scale, scale)
    this.group.position.set(x + groupPosition.x * scale, y + groupPosition.y * scale, 0)

    // apply x axis rotation globally to the group

    // Check if it's in viewport to render
    const isInViewport = rect.bottom >= 0 && rect.right >= 0 && rect.top <= windowSize.y && rect.left <= windowSize.x

    this.scrollProgress = 1 - rect.bottom / (windowSize.y + rect.height)
    this.scrollProgress = Math.min(Math.max(this.scrollProgress, 0), 1)
    if (reducedMotion) {
      this.scrollProgress = 0
    } else {
      this.scrollProgress = this.scrollProgress - 0.5
    }

    if (this.isAssetLoaded && !this.isAnimationStarted && isInViewport) {
      this.isAnimationStarted = true

      if (reducedMotion) {
        // Skip animation and set to final state
        this.tl.progress(1)
      } else {
        this.tl.play()
      }

      if (this.copilot) {
        if (reducedMotion) {
          this.copilot.tl.progress(1)
        } else {
          this.copilot.playTl()
        }
      }

      if (this.mona) {
        if (reducedMotion) {
          this.mona.tl.progress(1)
        } else {
          this.mona.playTl()
        }
      }

      if (this.invertocatCube) {
        if (reducedMotion) {
          this.invertocatCube.tl.progress(1)
        } else {
          this.invertocatCube.playTl()
        }
      }
    }

    if (isInViewport) {
      // mascot
      if (this.mona) {
        this.mona.update(this.scrollProgress)
      }

      // copilot
      if (this.copilot) {
        this.copilot.update(this.scrollProgress)
      }

      // rainbow cube
      if (this.rainbowMozaicCube) {
        this.rainbowMozaicCube.update()
      }

      // security cube
      if (this.securityCube) {
        this.securityCube.update(this.scrollProgress)
      }

      if (this.invertocatCube) {
        this.invertocatCube.update()
      }

      if (this.cubes) {
        this.cubes.update(scale, this.scrollProgress, groupPosition)
      }
    }
  }
}
